import React, { useEffect, useRef, useState, useCallback } from 'react';
import QrScanner from 'qr-scanner';
import { BrowserMultiFormatReader, NotFoundException, ChecksumException, FormatException } from '@zxing/library';
import { ScanResult, CameraScannerProps, ScannerState, CameraError, BarcodeFormat } from './types';
import { validateAustraliaPostTracking } from './trackingValidation';
import './CameraScanner.css';

export const CameraScanner: React.FC<CameraScannerProps> = ({
  isOpen,
  onClose,
  onScanSuccess,
  onScanError,
  timeoutMs = 30000,
  enableBarcodeScanning = true,
  validateAustraliaPost = true
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const zxingScannerRef = useRef<BrowserMultiFormatReader | null>(null);
  const [scannerState, setScannerState] = useState<ScannerState>(ScannerState.IDLE);
  const [error, setError] = useState<CameraError | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [debugInfo, setDebugInfo] = useState<string>('Component loaded');
  const scanningRef = useRef<boolean>(false);

  // Debug logging function
  const debugLog = useCallback((message: string) => {
    console.log(`[CameraScanner] ${message}`);
    setDebugInfo(prev => prev + `\n${new Date().toLocaleTimeString()}: ${message}`);
  }, []);

  // Map ZXing format to our BarcodeFormat
  const mapZxingFormat = (format: any): BarcodeFormat => {
    const formatStr = format?.toString() || '';
    switch (formatStr) {
      case 'QR_CODE': return 'QR_CODE';
      case 'CODE_128': return 'CODE_128';
      case 'CODE_39': return 'CODE_39';
      case 'EAN_13': return 'EAN_13';
      case 'EAN_8': return 'EAN_8';
      case 'UPC_A': return 'UPC_A';
      case 'UPC_E': return 'UPC_E';
      case 'ITF': return 'ITF';
      case 'CODABAR': return 'CODABAR';
      case 'DATA_MATRIX': return 'DATA_MATRIX';
      case 'PDF_417': return 'PDF_417';
      default: return 'UNKNOWN';
    }
  };

  const processScannedCode = useCallback((text: string, format: BarcodeFormat, source: 'qr-scanner' | 'zxing') => {
    if (scanningRef.current) {
      debugLog(`Code already being processed, ignoring duplicate from ${source}`);
      return;
    }

    scanningRef.current = true;
    debugLog(`Code scanned by ${source}: ${text} (${format})`);

    // Validate tracking number if enabled
    let isValidTracking = false;
    let trackingInfo = undefined;

    if (validateAustraliaPost) {
      const validation = validateAustraliaPostTracking(text);
      isValidTracking = validation.isValid;
      if (validation.isValid) {
        trackingInfo = {
          carrier: validation.carrier,
          format: validation.format,
          description: validation.description
        };
        debugLog(`Valid ${validation.carrier} tracking number detected: ${validation.format}`);
      }
    }

    const scanResult: ScanResult = {
      text,
      format,
      timestamp: Date.now(),
      isValidTracking,
      trackingInfo
    };

    setScannerState(ScannerState.SUCCESS);
    onScanSuccess(scanResult);

    // Auto-close after successful scan
    setTimeout(() => {
      onClose();
    }, 500);
  }, [onScanSuccess, onClose, debugLog, validateAustraliaPost]);

  const handleQrScanSuccess = useCallback((result: QrScanner.ScanResult) => {
    processScannedCode(result.data, 'QR_CODE', 'qr-scanner');
  }, [processScannedCode]);

  const handleScanError = useCallback((error: string | Error) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    
    // Only handle actual errors, not "no code found" messages
    if (errorMessage && 
        !errorMessage.includes('No QR code found') &&
        !errorMessage.includes('Could not decode QR Code') &&
        !errorMessage.includes('NotFoundException')) {
      
      debugLog(`Scanner error: ${errorMessage}`);
      
      const cameraError: CameraError = {
        code: 'SCAN_ERROR',
        message: errorMessage,
        userMessage: 'Unable to scan code. Please try again or enter manually.'
      };
      
      setError(cameraError);
      setScannerState(ScannerState.ERROR);
    }
  }, [debugLog]);

  const startZxingScanning = useCallback(async () => {
    if (!enableBarcodeScanning || !videoRef.current || zxingScannerRef.current) {
      return;
    }

    try {
      debugLog('Starting ZXing barcode scanner...');
      const codeReader = new BrowserMultiFormatReader();
      zxingScannerRef.current = codeReader;

      // Start continuous scanning
      const stream = videoRef.current.srcObject as MediaStream;
      if (stream) {
        codeReader.decodeFromStream(stream, videoRef.current, (result: any, error: any) => {
          if (result) {
            const format = mapZxingFormat(result.getBarcodeFormat());
            processScannedCode(result.getText(), format, 'zxing');
          } else if (error && !(error instanceof NotFoundException)) {
            // Only log non-NotFoundException errors
            if (!(error instanceof ChecksumException) && !(error instanceof FormatException)) {
              debugLog(`ZXing error: ${error.message}`);
            }
          }
        });
        
        debugLog('ZXing scanner started successfully');
      }
    } catch (err: any) {
      debugLog(`ZXing initialization error: ${err.message}`);
    }
  }, [enableBarcodeScanning, processScannedCode, debugLog]);

  const initializeScanner = useCallback(async () => {
    debugLog('initializeScanner called');
    debugLog(`isOpen: ${isOpen}, videoRef.current: ${!!videoRef.current}`);
    
    if (!isOpen) {
      debugLog('Scanner not open, returning');
      return;
    }
    
    if (!videoRef.current) {
      debugLog('Video ref not available, returning');
      return;
    }
    
    if (qrScannerRef.current || zxingScannerRef.current) {
      debugLog('Scanner already exists, returning');
      return;
    }

    debugLog('Starting scanner initialization...');
    setScannerState(ScannerState.INITIALIZING);
    setError(null);
    scanningRef.current = false;

    try {
      // Check if QrScanner is available
      debugLog('Checking QrScanner availability...');
      if (typeof QrScanner === 'undefined') {
        throw new Error('QrScanner library not loaded');
      }

      // Check if camera is supported
      debugLog('Checking camera availability...');
      const hasCamera = await QrScanner.hasCamera();
      debugLog(`Camera available: ${hasCamera}`);
      
      if (!hasCamera) {
        const cameraError: CameraError = {
          code: 'NO_CAMERA',
          message: 'No camera found',
          userMessage: 'No camera found on this device. Please enter the tracking number manually.'
        };
        debugLog('No camera found');
        setError(cameraError);
        setScannerState(ScannerState.NO_CAMERA);
        onScanError?.(cameraError.message);
        return;
      }

      // Create qr-scanner instance for QR codes
      debugLog('Creating QrScanner instance...');
      const qrScanner = new QrScanner(
        videoRef.current,
        handleQrScanSuccess,
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment',
          maxScansPerSecond: 5,
        }
      );

      debugLog('QrScanner instance created');
      qrScannerRef.current = qrScanner;

      // Start QR scanning
      debugLog('Starting QR scanner...');
      await qrScanner.start();
      debugLog('QR Scanner started successfully');

      // Start ZXing barcode scanning if enabled
      if (enableBarcodeScanning) {
        await startZxingScanning();
      }

      setScannerState(ScannerState.SCANNING);

      // Set timeout for scanning
      timeoutRef.current = setTimeout(() => {
        debugLog('Scanner timeout reached');
        const timeoutError: CameraError = {
          code: 'TIMEOUT',
          message: 'Scanning timeout',
          userMessage: 'Scanning timed out. Please try again or enter the tracking number manually.'
        };
        setError(timeoutError);
        setScannerState(ScannerState.ERROR);
      }, timeoutMs);

    } catch (err: any) {
      debugLog(`Scanner initialization error: ${err.message || err}`);
      console.error('Scanner initialization error:', err);
      
      let cameraError: CameraError;
      
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        cameraError = {
          code: 'NotAllowedError',
          message: err.message || 'Permission denied',
          userMessage: 'Camera access denied. Please enable camera permissions in your browser settings and try again.'
        };
        setScannerState(ScannerState.PERMISSION_DENIED);
      } else if (err.name === 'NotFoundError' || err.message?.includes('No camera found')) {
        cameraError = {
          code: 'NO_CAMERA',
          message: err.message || 'No camera found',
          userMessage: 'No camera found on this device. Please enter the tracking number manually.'
        };
        setScannerState(ScannerState.NO_CAMERA);
      } else if (err.name === 'NotSupportedError' || err.message?.includes('not supported')) {
        cameraError = {
          code: 'NO_CAMERA',
          message: err.message || 'Camera not supported',
          userMessage: 'Camera not supported on this browser. Please enter the tracking number manually.'
        };
        setScannerState(ScannerState.NO_CAMERA);
      } else {
        cameraError = {
          code: 'SCANNER_ERROR',
          message: err.message || 'Scanner initialization error',
          userMessage: `Unable to initialize camera scanner: ${err.message || 'Unknown error'}. Please try again or enter manually.`
        };
        setScannerState(ScannerState.ERROR);
      }
      
      setError(cameraError);
      onScanError?.(cameraError.message);
    }
  }, [isOpen, timeoutMs, handleQrScanSuccess, onScanError, debugLog, enableBarcodeScanning, startZxingScanning]);

  const cleanup = useCallback(() => {
    debugLog('Cleaning up scanners...');
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (qrScannerRef.current) {
      try {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
      } catch (err) {
        console.warn('Error cleaning up QR scanner:', err);
      }
      qrScannerRef.current = null;
    }

    if (zxingScannerRef.current) {
      try {
        zxingScannerRef.current.reset();
      } catch (err) {
        console.warn('Error cleaning up ZXing scanner:', err);
      }
      zxingScannerRef.current = null;
    }
    
    scanningRef.current = false;
    setScannerState(ScannerState.IDLE);
    setError(null);
  }, [debugLog]);

  useEffect(() => {
    if (isOpen) {
      initializeScanner();
    } else {
      cleanup();
    }

    return cleanup;
  }, [isOpen, initializeScanner, cleanup]);

  const handleRetry = () => {
    cleanup();
    setTimeout(() => {
      initializeScanner();
    }, 100);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="camera-scanner-modal">
      <div className="scanner-overlay">
        <div className="scanner-header">
          <h2 id="scanner-title" className="scanner-title">
            Scan {enableBarcodeScanning ? 'QR Code or Barcode' : 'QR Code'}
          </h2>
          <button 
            className="scanner-close-btn"
            onClick={onClose}
            aria-label="Close scanner"
          >
            ×
          </button>
        </div>

        <div className="scanner-content">
          {/* Feature indicators */}
          <div style={{
            padding: '8px',
            background: '#f8f9fa',
            margin: '8px 0',
            borderRadius: '4px',
            fontSize: '12px',
            textAlign: 'center',
            color: '#666'
          }}>
            <div>
              {enableBarcodeScanning ? '📱 QR Codes & Barcodes' : '📱 QR Codes Only'}
              {validateAustraliaPost && ' • 🇦🇺 Australia Post Validation'}
            </div>
          </div>

          {/* Always render video element */}
          <video 
            ref={videoRef} 
            className="qr-video"
            style={{
              width: '100%',
              height: '300px',
              objectFit: 'cover',
              borderRadius: '8px',
              background: '#000',
              display: 'block'
            }}
            playsInline
            muted
            autoPlay
          />

          {/* State-based content */}
          {scannerState === ScannerState.INITIALIZING && (
            <div className="scanner-loading">
              <div className="loading-spinner"></div>
              <p>Initializing camera...</p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                {enableBarcodeScanning ? 'Setting up QR code and barcode scanning...' : 'Setting up QR code scanning...'}
              </p>
            </div>
          )}

          {scannerState === ScannerState.SCANNING && (
            <div className="scanner-active">
              <div className="scanner-instructions">
                <p>Point your camera at the {enableBarcodeScanning ? 'QR code or barcode' : 'QR code'}</p>
                <p className="scanner-tip">Make sure the code is well-lit and clearly visible</p>
                {validateAustraliaPost && (
                  <p style={{ fontSize: '12px', color: '#007bff', marginTop: '8px' }}>
                    ✓ Australia Post tracking validation enabled
                  </p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="scanner-error">
              <div className="error-icon">⚠️</div>
              <p className="error-message">{error.userMessage}</p>
              
              {error.code === 'NotAllowedError' && (
                <div className="permission-help" style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  textAlign: 'left'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>To enable camera access:</p>
                  <p style={{ margin: '4px 0' }}><strong>Chrome Mobile:</strong> Tap the camera icon in the address bar and select "Allow"</p>
                  <p style={{ margin: '4px 0' }}><strong>Safari Mobile:</strong> Go to Settings → Safari → Camera and allow access for this site</p>
                  <p style={{ margin: '4px 0' }}><strong>Desktop:</strong> Click the camera icon in your browser's address bar and allow access</p>
                </div>
              )}
              
              {scannerState !== ScannerState.NO_CAMERA && (
                <button className="retry-btn" onClick={handleRetry}>
                  Try Again
                </button>
              )}
              <button className="manual-entry-btn" onClick={onClose}>
                Enter Manually
              </button>
            </div>
          )}

          {scannerState === ScannerState.SUCCESS && (
            <div className="scanner-success">
              <div className="success-icon">✓</div>
              <p>Code scanned successfully!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};